import { authService } from './authService';
import { hasAllPermissions, hasAnyPermission, hasPermission } from '../utils/permissions';

export type PermissionCode = string;

export interface UserPermissions {
  [key: string]: boolean;
}

class PermissionService {
  private permissions: UserPermissions = {};
  private loaded: boolean = false;

  /**
   * Load user permissions from the backend
   */
  async loadPermissions(): Promise<void> {
    try {
      const response = await authService.getProfile();
      if (response.success && response.user) {
        // For now, we'll assume the user object contains role information
        // In a real implementation, you might want to create a specific endpoint
        // to get user permissions
        this.permissions = await this.fetchUserPermissions(response.user);
        this.loaded = true;
      }
    } catch (error) {
      console.error('Failed to load permissions:', error);
      this.permissions = {};
      this.loaded = false;
    }
  }

  /**
   * Fetch user permissions from backend
   * This would typically call a dedicated permissions endpoint
   */
  private async fetchUserPermissions(user: any): Promise<UserPermissions> {
    return (user.permissions || []).reduce((permissions: UserPermissions, code: string) => {
      permissions[code] = true;
      return permissions;
    }, {});
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: PermissionCode): boolean {
    return hasPermission(Object.keys(this.permissions), permission);
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissions: PermissionCode[]): boolean {
    return hasAnyPermission(Object.keys(this.permissions), permissions);
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(permissions: PermissionCode[]): boolean {
    return hasAllPermissions(Object.keys(this.permissions), permissions);
  }

  /**
   * Get all user permissions
   */
  getPermissions(): UserPermissions {
    return { ...this.permissions };
  }

  /**
   * Check if permissions are loaded
   */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Clear permissions (useful for logout)
   */
  clearPermissions(): void {
    this.permissions = {};
    this.loaded = false;
  }
}

export const permissionService = new PermissionService();
